/**
 * MobileWidgetDrawer
 *
 * FAB (green) → bottom dock with a horizontal scrollable icon bar.
 * Clicking an icon expands that widget upward above the bar.
 */
import React, { Suspense, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Fab,
  Backdrop,
  Slide,
  Paper,
  Collapse,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  BarChart as BarChartIcon,
  ShowChart as ShowChartIcon,
  Whatshot as WhatshotIcon,
} from "@mui/icons-material";
import { MarketOverview, StockMarket } from "react-tradingview-embed";
import StockHeatMap from "./StockHeatMap";
import { useTheme } from "./ThemeContext";
import ErrorBoundary from "../utils/ErrorBoundary";

// Height of the horizontal dock bar (drag handle + icon row + padding)
const DOCK_HEIGHT = 96;
// Height of the sticky widget header (title + close button row)
const WIDGET_HEADER_HEIGHT = 52;
// Extra breathing room at the bottom
const BOTTOM_MARGIN = 12;

/**
 * Returns the pixel height the TradingView widget should render at,
 * filling all the space between the dock and the top of the viewport.
 */
function useWidgetHeight() {
  const [height, setHeight] = useState(() =>
    Math.max(300, window.innerHeight - DOCK_HEIGHT - WIDGET_HEADER_HEIGHT - BOTTOM_MARGIN)
  );

  useEffect(() => {
    const update = () =>
      setHeight(
        Math.max(300, window.innerHeight - DOCK_HEIGHT - WIDGET_HEADER_HEIGHT - BOTTOM_MARGIN)
      );
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return height;
}

const DEFAULT_WIDGET_OPTIONS = [
  {
    id: "heatmap",
    label: "Heatmap",
    icon: WhatshotIcon,
    activeColor: "#ff8e53",
    activeBg: "rgba(255,142,83,0.12)",
    activeBorder: "rgba(255,142,83,0.35)",
  },
  {
    id: "market",
    label: "Market",
    icon: BarChartIcon,
    activeColor: "#38bdf8",
    activeBg: "rgba(56,189,248,0.12)",
    activeBorder: "rgba(56,189,248,0.35)",
  },
  {
    id: "movers",
    label: "Movers",
    icon: TrendingUpIcon,
    activeColor: "#05df72",
    activeBg: "rgba(0,201,80,0.12)",
    activeBorder: "rgba(0,201,80,0.35)",
  },
];

// Built-in renderer for default widgets.
// Custom widgets supply their own render(height, theme) instead.
function WidgetContent({ id, theme, height }) {
  if (id === "heatmap") return <StockHeatMap />;

  if (id === "market")
    return (
      <MarketOverview
        widgetProps={{
          colorTheme: "dark",
          dateRange: "3M",
          showChart: true,
          locale: "en",
          width: "100%",
          height,
          showSymbolLogo: true,
          showFloatingTooltip: true,
          isTransparent: true,
          plotLineColorGrowing: "rgba(0,255,0,1)",
          plotLineColorFalling: "rgba(255,0,0,1)",
          gridLineColor: "rgba(240,243,250,0)",
          scaleFontColor: "rgba(106,109,120,1)",
          belowLineFillColorGrowing: "rgba(41,98,255,0.12)",
          belowLineFillColorFalling: "rgba(41,98,255,0.12)",
          belowLineFillColorGrowingBottom: "rgba(147,196,125,0)",
          belowLineFillColorFallingBottom: "rgba(41,98,255,0)",
          symbolActiveColor: "rgba(41,98,255,0.12)",
          tabs: [
            {
              title: "Indices",
              symbols: [
                { s: "FOREXCOM:SPXUSD", d: "S&P 500" },
                { s: "NASDAQ:NDX", d: "NASDAQ 100" },
                { s: "FOREXCOM:DJI", d: "Dow Jones" },
                { s: "INDEX:NKY", d: "Nikkei 225" },
                { s: "INDEX:DEU40", d: "DAX" },
                { s: "FOREXCOM:UKXGBP", d: "FTSE 100" },
              ],
              originalTitle: "Indices",
            },
            {
              title: "Futures",
              symbols: [
                { s: "CME_MINI:ES1!", d: "S&P 500" },
                { s: "CME:6E1!", d: "Euro" },
                { s: "COMEX:GC1!", d: "Gold" },
                { s: "NYMEX:CL1!", d: "WTI Crude" },
              ],
              originalTitle: "Futures",
            },
            {
              title: "Forex",
              symbols: [
                { s: "FX:EURUSD", d: "EUR/USD" },
                { s: "FX:GBPUSD", d: "GBP/USD" },
                { s: "FX:USDJPY", d: "USD/JPY" },
                { s: "FX:USDCAD", d: "USD/CAD" },
              ],
              originalTitle: "Forex",
            },
          ],
        }}
      />
    );

  if (id === "movers")
    return (
      <Suspense
        fallback={
          <Box sx={{ p: 3, color: theme.palette.text.secondary }}>
            Loading…
          </Box>
        }
      >
        <StockMarket
          widgetProps={{
            colorTheme: "dark",
            dateRange: "12M",
            exchange: "US",
            showChart: true,
            locale: "en",
            isTransparent: true,
            showSymbolLogo: false,
            showFloatingTooltip: false,
            width: "100%",
            height,
            plotLineColorGrowing: "rgba(41,255,209,1)",
            plotLineColorFalling: "rgba(255,41,41,1)",
            gridLineColor: "rgba(240,243,250,0)",
            scaleFontColor: "rgba(106,109,120,1)",
            belowLineFillColorGrowing: "rgba(41,98,255,0.12)",
            belowLineFillColorFalling: "rgba(41,98,255,0.12)",
            belowLineFillColorGrowingBottom: "rgba(41,98,255,0)",
            belowLineFillColorFallingBottom: "rgba(41,98,255,0)",
            symbolActiveColor: "rgba(41,98,255,0.12)",
            largeChartUrl: "www.stonkscentral.com/s/{tvexchange}-{tvsymbol}",
          }}
        />
      </Suspense>
    );

  return null;
}

export default function MobileWidgetDrawer({ widgets }) {
  const { theme } = useTheme();
  const [dockOpen, setDockOpen] = useState(false);
  const [activeWidget, setActiveWidget] = useState(null);
  const widgetHeight = useWidgetHeight();

  // Use caller-supplied widgets or fall back to defaults
  const widgetOptions = widgets || DEFAULT_WIDGET_OPTIONS;

  // TradingView widgets load cross-origin scripts whose errors are reported as
  // "Script error." with no stack — the browser strips details due to CORS.
  // CRA's dev overlay catches every window error, including these harmless ones.
  // We intercept them in the capture phase (before the overlay handler) and
  // log them quietly instead of letting them bubble up as uncaught errors.
  useEffect(() => {
    const handleCrossOriginError = (event) => {
      if (
        event.message === "Script error." ||
        event.message === "Script error" ||
        (event.message === "" && !event.filename)
      ) {
        event.stopImmediatePropagation();
        event.preventDefault();
        console.log(
          "[MobileWidgetDrawer] Suppressed cross-origin script error from TradingView widget",
          { source: event.filename || "unknown", lineno: event.lineno }
        );
        return false;
      }
    };

    // Capture phase so we run before CRA's error overlay listener
    window.addEventListener("error", handleCrossOriginError, true);
    return () => {
      window.removeEventListener("error", handleCrossOriginError, true);
    };
  }, []);

  const handleFab = () => {
    if (dockOpen) {
      setDockOpen(false);
      setActiveWidget(null);
    } else {
      setDockOpen(true);
    }
  };

  const handleIconPress = (id) => {
    setActiveWidget((prev) => (prev === id ? null : id));
  };

  const handleCloseWidget = () => setActiveWidget(null);

  const activeOption = widgetOptions.find((w) => w.id === activeWidget);

  return (
    <>
      {/* ── FAB ──────────────────────────────────────────────────────── */}
      <Fab
        onClick={handleFab}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1200,
          background: dockOpen
            ? "linear-gradient(135deg, #009966 0%, #007a50 100%)"
            : "linear-gradient(135deg, #00c950 0%, #009966 100%)",
          color: "#fff",
          boxShadow: "0 8px 24px rgba(0,201,80,0.4)",
          "&:hover": {
            background: "linear-gradient(135deg, #009966 0%, #007a50 100%)",
            transform: "scale(1.05)",
          },
          transition: "all 0.2s ease",
        }}
      >
        {dockOpen ? (
          <CloseIcon sx={{ fontSize: 24 }} />
        ) : (
          <ShowChartIcon sx={{ fontSize: 26 }} />
        )}
      </Fab>

      {/* ── Backdrop ─────────────────────────────────────────────────── */}
      <Backdrop
        open={dockOpen}
        onClick={handleFab}
        sx={{ zIndex: 1250, backgroundColor: "rgba(0,0,0,0.5)" }}
      />

      {/* ── Bottom dock ──────────────────────────────────────────────── */}
      <Slide direction="up" in={dockOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            backgroundColor: "#0f1419",
            borderTop: "1px solid rgba(255,255,255,0.07)",
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
            overflow: "hidden",
            maxHeight: "100dvh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* ── Expanded widget panel (slides in above the dock) ── */}
          {/* maxHeight = viewport minus the dock bar (~100px) minus a small gap */}
          <Collapse in={Boolean(activeWidget)} timeout={300}>
            {activeWidget && (
              <Box
                sx={{
                  borderBottom: "1px solid rgba(255,255,255,0.07)",
                  overflowX: "hidden",
                }}
              >
                {/* Widget header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.25,
                    backgroundColor: "#1a1f26",
                    borderBottom: "1px solid rgba(255,255,255,0.06)",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      color: activeOption?.activeColor || "#05df72",
                    }}
                  >
                    {activeOption?.label}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleCloseWidget}
                    sx={{
                      color: "rgba(255,255,255,0.4)",
                      p: 0.5,
                      borderRadius: "8px",
                      "&:hover": {
                        color: "#ff6467",
                        backgroundColor: "rgba(251,44,54,0.08)",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Box>

                {/* Widget content — custom render fn or built-in */}
                <ErrorBoundary>
                  {activeOption?.render
                    ? activeOption.render(widgetHeight, theme)
                    : <WidgetContent id={activeWidget} theme={theme} height={widgetHeight} />
                  }
                </ErrorBoundary>
              </Box>
            )}
          </Collapse>

          {/* ── Horizontal icon dock — always visible at the bottom ── */}
          <Box sx={{ pb: "env(safe-area-inset-bottom, 0px)", flexShrink: 0 }}>
            {/* Drag handle */}
            <Box
              sx={{ display: "flex", justifyContent: "center", pt: 1.25, pb: 0.5 }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 4,
                  backgroundColor: alpha("#fff", 0.15),
                  borderRadius: 2,
                }}
              />
            </Box>

            {/* Scrollable icon row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                pb: 2,
                overflowX: "auto",
                // Hide scrollbar but keep scrolling
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {widgetOptions.map((option) => {
                const Icon = option.icon;
                const isActive = activeWidget === option.id;

                return (
                  <Box
                    key={option.id}
                    onClick={() => handleIconPress(option.id)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.6,
                      px: 2,
                      py: 1.25,
                      minWidth: 76,
                      borderRadius: "14px",
                      cursor: "pointer",
                      flexShrink: 0,
                      backgroundColor: isActive
                        ? option.activeBg
                        : "rgba(255,255,255,0.04)",
                      border: `1px solid ${
                        isActive ? option.activeBorder : "rgba(255,255,255,0.07)"
                      }`,
                      transition: "all 0.18s ease",
                      "&:hover": {
                        backgroundColor: isActive
                          ? option.activeBg
                          : "rgba(255,255,255,0.07)",
                      },
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 22,
                        color: isActive ? option.activeColor : "rgba(255,255,255,0.5)",
                        transition: "color 0.18s ease",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? option.activeColor : "rgba(255,255,255,0.45)",
                        letterSpacing: "0.01em",
                        transition: "color 0.18s ease",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Paper>
      </Slide>
    </>
  );
}
